import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, Eye, X } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCsv } from '../../utils/exportCsv';
import type { BackOfficeContentRow, BackOfficeContentStats } from '@crewcast/shared';

function monthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function getAssetType(url: string): 'image' | 'video' | 'other' {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) return 'video';
  return 'other';
}

export default function BackOfficeContentPage() {
  const { token } = useAuth();
  const now = new Date();
  const [rows, setRows] = useState<BackOfficeContentRow[]>([]);
  const [stats, setStats] = useState<BackOfficeContentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStart(now));
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));
  const [status, setStatus] = useState('all');
  const [companies, setCompanies] = useState<{ companyId: string; displayName: string }[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [previewRow, setPreviewRow] = useState<BackOfficeContentRow | null>(null);

  useEffect(() => {
    async function loadCompanies() {
      try {
        if (!token) return;
        const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?pageSize=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) setCompanies(data.companies || []);
      } catch {
        // Non-fatal
      }
    }
    loadCompanies();
  }, []);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      if (!token) throw new Error('Not authenticated');

      const params = new URLSearchParams({ dateFrom, dateTo, status });
      if (companyId) params.set('companyId', companyId);

      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/content?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load content data');
      setRows(data.rows || []);
      setStats(data.stats || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load content data');
    } finally {
      setLoading(false);
    }
  }

  function handleApply(e: React.FormEvent) {
    e.preventDefault();
    fetchData();
  }

  function handleExport() {
    exportToCsv(
      `crewcast-content-${dateFrom}-${dateTo}`,
      ['submissionId', 'companyName', 'challengeTitle', 'userEmail', 'submittedAt', 'status'],
      rows as unknown as Record<string, unknown>[]
    );
  }

  return (
    <>
      <div className="bo-page-header">
        <h1 className="bo-page-title">Content Pipeline</h1>
        <p className="bo-page-subtitle">Submission volume and moderation status</p>
      </div>

      {/* Summary */}
      {stats && !loading && (
        <div className="bo-summary-row">
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Total</div>
            <div className="bo-summary-item-value">{stats.total}</div>
          </div>
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Approved</div>
            <div className="bo-summary-item-value" style={{ color: 'var(--color-success)' }}>{stats.approved}</div>
          </div>
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Pending</div>
            <div className="bo-summary-item-value" style={{ color: 'var(--color-warning)' }}>{stats.pending}</div>
          </div>
          <div className="bo-summary-item">
            <div className="bo-summary-item-label">Rejected</div>
            <div className="bo-summary-item-value" style={{ color: 'var(--color-error)' }}>{stats.rejected}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <form className="bo-filters" onSubmit={handleApply}>
        <div className="bo-filter-group">
          <label className="bo-filter-label">From</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">To</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Account</label>
          <select
            className="bo-filter-select crewcast-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">All accounts</option>
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
            ))}
          </select>
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Status</label>
          <select
            className="bo-filter-select crewcast-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="bo-filter-actions">
          <button type="submit" className="bo-btn bo-btn-primary" disabled={loading}>
            Apply
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} />
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={handleExport} disabled={loading || rows.length === 0}>
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">
            Submissions
            <span className="bo-table-count">({rows.length})</span>
          </span>
        </div>

        {loading ? (
          <div className="bo-loading">Loading...</div>
        ) : error ? (
          <div className="bo-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="bo-empty">No submissions match the selected filters.</div>
        ) : (
          <div className="bo-table-wrapper">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Moment</th>
                  <th>Submitted By</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.submissionId}>
                    <td>{r.companyName}</td>
                    <td>{r.challengeTitle}</td>
                    <td>{r.userEmail ?? r.userId}</td>
                    <td>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '—'}</td>
                    <td>
                      {r.status === 'approved' && <span className="bo-badge bo-badge-green">Approved</span>}
                      {r.status === 'pending'  && <span className="bo-badge bo-badge-yellow">Pending</span>}
                      {r.status === 'rejected' && <span className="bo-badge bo-badge-red">Rejected</span>}
                    </td>
                    <td>
                      {r.firstAssetUrl ? (
                        <button
                          className="bo-btn bo-btn-secondary"
                          style={{ padding: '4px 8px' }}
                          onClick={() => setPreviewRow(r)}
                          title="Preview submission"
                        >
                          <Eye size={14} />
                        </button>
                      ) : (
                        <span style={{ color: 'var(--color-gray-400)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewRow && (
        <div className="bo-modal-overlay" onClick={() => setPreviewRow(null)}>
          <div className="bo-modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div className="bo-modal-header">
              <div>
                <div className="bo-modal-title">{previewRow.challengeTitle}</div>
                <div className="bo-modal-subtitle">{previewRow.companyName} · {previewRow.userEmail ?? previewRow.userId}</div>
              </div>
              <button className="bo-modal-close" onClick={() => setPreviewRow(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="bo-modal-body" style={{ display: 'flex', justifyContent: 'center', background: 'var(--color-gray-950, #0a0a0a)', borderRadius: 8, overflow: 'hidden', maxHeight: '70vh' }}>
              {(() => {
                const assetType = getAssetType(previewRow.firstAssetUrl!);
                if (assetType === 'image') {
                  return (
                    <img
                      src={previewRow.firstAssetUrl}
                      alt="Submission"
                      style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    />
                  );
                }
                if (assetType === 'video') {
                  return (
                    <video
                      src={previewRow.firstAssetUrl}
                      controls
                      playsInline
                      style={{ maxWidth: '100%', maxHeight: '70vh' }}
                    />
                  );
                }
                return (
                  <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-gray-300)' }}>
                    <p>Cannot preview this file type.</p>
                    <a
                      href={previewRow.firstAssetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="bo-btn bo-btn-primary"
                      style={{ display: 'inline-block', marginTop: 12 }}
                    >
                      Open file
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
